import React, { useEffect, useRef } from 'react';

// Skip to main content link for screen readers
export const SkipLink = () => (
  <a 
    href="#main-content" 
    className="skip-link"
    style={{
      position: 'absolute',
      top: '-40px',
      left: '6px',
      background: '#3F0A1C',
      color: '#F8DEB3',
      padding: '8px',
      textDecoration: 'none',
      zIndex: 10000,
      borderRadius: '4px',
      fontSize: '14px',
      fontWeight: '600'
    }}
    onFocus={(e) => {
      e.target.style.top = '6px';
    }}
    onBlur={(e) => {
      e.target.style.top = '-40px';
    }}
  >
    Skip to main content
  </a>
);

// Focus trap for modals
export const FocusTrap = ({ children, isActive = false }) => {
  const containerRef = useRef(null);
  const firstFocusableRef = useRef(null);
  const lastFocusableRef = useRef(null);

  useEffect(() => {
    if (!isActive) return;

    const container = containerRef.current;
    if (!container) return;

    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    firstFocusableRef.current = focusableElements[0];
    lastFocusableRef.current = focusableElements[focusableElements.length - 1];

    const handleKeyDown = (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstFocusableRef.current) {
            e.preventDefault();
            lastFocusableRef.current.focus();
          }
        } else {
          if (document.activeElement === lastFocusableRef.current) {
            e.preventDefault();
            firstFocusableRef.current.focus();
          }
        }
      }
    };

    container.addEventListener('keydown', handleKeyDown);
    firstFocusableRef.current.focus();

    return () => {
      container.removeEventListener('keydown', handleKeyDown);
    };
  }, [isActive]);

  return (
    <div ref={containerRef}>
      {children}
    </div>
  );
};

// Announce changes to screen readers
export const LiveRegion = ({ message, ariaLive = 'polite' }) => (
  <div
    aria-live={ariaLive}
    aria-atomic="true"
    className="sr-only"
    style={{
      position: 'absolute',
      left: '-10000px',
      width: '1px',
      height: '1px',
      overflow: 'hidden'
    }}
  >
    {message}
  </div>
);

// Screen reader only text
export const SrOnly = ({ children }) => (
  <span
    className="sr-only"
    style={{
      position: 'absolute',
      left: '-10000px',
      width: '1px',
      height: '1px',
      overflow: 'hidden'
    }}
  >
    {children}
  </span>
);

// Accessible button with proper ARIA labels
export const AccessibleButton = ({ 
  children, 
  onClick, 
  ariaLabel, 
  ariaDescribedBy,
  disabled = false,
  className = '',
  ...props 
}) => (
  <button
    onClick={onClick}
    aria-label={ariaLabel}
    aria-describedby={ariaDescribedBy}
    disabled={disabled}
    className={className}
    {...props}
  >
    {children}
  </button>
);

// Accessible image with proper alt text
export const AccessibleImage = ({ 
  src, 
  alt, 
  className = '', 
  ...props 
}) => (
  <img
    src={src}
    alt={alt || 'Decorative image'}
    className={className}
    loading="lazy"
    {...props}
  />
);

// Accessible form field with proper labels
export const AccessibleFormField = ({ 
  id, 
  label, 
  error, 
  required = false,
  children 
}) => (
  <div className="form-field">
    <label htmlFor={id} className="form-label">
      {label}
      {required && <span aria-label="required"> *</span>}
    </label>
    {children}
    {error && (
      <div id={`${id}-error`} className="form-error" role="alert" aria-live="polite">
        {error}
      </div>
    )}
  </div>
);

export default {
  SkipLink,
  FocusTrap,
  LiveRegion,
  SrOnly,
  AccessibleButton,
  AccessibleImage,
  AccessibleFormField
};
